import type { FC, ReactNode } from 'react'

import { ArrowUpRight } from 'lucide-react'
import { buttonClass } from '@/lib/ui'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Media } from '@/components/generals/media'
import { Reveal } from '@/components/generals/reveal'
import { ArcDivider, RingsDivider } from '@/components/generals/hairline'
import { VisitHero } from '@/components/heroes/visit-hero'
import { VisitMap } from '@/components/visit/visit-map'
import { ContactForm } from '@/components/visit/contact-form'
import { OBSERVATORY, OPENING_HOURS, formatCoordinate } from '@/constants/observatory'
import { siteConfig } from '@/constants/site'

const TRAVEL = [
    { title: 'By car', text: 'From the M5 at junction 27, take the A361 and then the A396 north through Dulverton to Wheddon Cross, about fifty minutes. Turn west onto the B3224 and follow the brown signs up Hollowmoor Lane.' },
    { title: 'By train', text: 'The nearest mainline station is Taunton, about an hour away by car or taxi. In summer the West Somerset Railway runs heritage steam trains to Minehead, twenty minutes north.' },
    { title: 'By bus', text: 'Daytime buses stop at Wheddon Cross, a twenty-minute walk away up a quiet lane. Moorland buses do not run after dark, so plan evening visits by car or taxi.' }
]

const ACCESS = [
    'Step-free routes from the car park to the dome, exhibition, café and toilets.',
    'A lift to the roof deck, with a lowered eyepiece on our refractor for seated observers.',
    'Four wheelchair spaces with companion seats in the planetarium, beside the entrance.',
    'Hearing loop at the front desk and in the dome; captioned versions of every film show.',
    'Relaxed shows on the first Sunday of each month, with the lights a little higher and the sound a little lower.',
    'Assistance dogs welcome everywhere except the roof deck after dark, where we will gladly look after them indoors.'
]

const ETIQUETTE = [
    { title: 'Red torches only', text: 'White light ruins everyone’s night vision for twenty minutes. We lend red torches at the desk.' },
    { title: 'Sidelights on the lane', text: 'Please dip to sidelights for the last half mile after dark. Headlights sweep straight across the telescopes.' },
    { title: 'Phones in red, or away', text: 'Turn on red-light mode on this site, or put a red filter on your screen. No flash photography on the deck.' }
]

const Visit: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Visit'
                description='How to find CosmicLens Observatory on Exmoor: address, opening hours, parking, accessibility, what to wear on the roof deck, and how to contact us.'
            />

            <VisitHero contentId='visit-intro' />

            <section id='visit-intro' className='container-page scroll-mt-20 pt-10 outline-none md:pt-14'>
                <Reveal variant='wipe' className='max-w-xl'>
                    <p className='text-lg text-star-2'>
                        We are on Hollowmoor Ridge in Exmoor National Park, Europe&apos;s first International Dark Sky Reserve. On a clear, moonless night the Milky Way stretches from one horizon to the other.
                    </p>
                </Reveal>
            </section>

            <section aria-labelledby='where-title' className='container-page grid gap-14 py-24 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <p className='label-mono text-star-3'>
                        Where we are
                    </p>
                    <h2 id='where-title' className='display-serif mt-3 text-5xl text-star'>
                        Hollowmoor Ridge
                    </h2>
                    <address className='mt-8 not-italic'>
                        <p className='text-lg text-star'>
                            {OBSERVATORY.name}
                        </p>
                        {OBSERVATORY.addressLines.map(line => (
                            <p key={line} className='text-star-2'>
                                {line}
                            </p>
                        ))}
                        <p className='data-mono mt-3 text-star-3'>
                            {`${formatCoordinate(OBSERVATORY.latitude, 'N', 'S')}   ${formatCoordinate(OBSERVATORY.longitude, 'E', 'W')}`}
                        </p>
                        <p className='mt-4 text-star-2'>
                            {`Desk: ${siteConfig.phone}`}
                        </p>
                    </address>
                    <p className='mt-6 text-[0.95rem] text-star-3'>
                        Postcodes cover several square miles of moor here, and some sat-navs send drivers down farm tracks. Use the coordinates above, or follow the brown observatory signs from Wheddon Cross.
                    </p>
                    <a href={OBSERVATORY.directionsUrl} target='_blank' rel='noreferrer' className={buttonClass('primary', 'mt-8')}>
                        Get directions
                        <ArrowUpRight aria-hidden className='size-4' strokeWidth={1.25} />
                    </a>
                </div>
                <Reveal className='lg:col-span-7'>
                    <VisitMap />
                </Reveal>
            </section>

            <section id='hours' aria-labelledby='hours-title' className='container-page scroll-mt-24 grid gap-14 pb-24 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <h2 id='hours-title' className='display-serif text-5xl text-star'>
                        Opening hours
                    </h2>
                    <p className='mt-4 text-star-2'>
                        Shows run through the day. On late nights the roof deck opens after dark for booked telescope nights.
                    </p>
                </div>
                <div className='lg:col-span-7'>
                    <dl>
                        {OPENING_HOURS.map(row => (
                            <div key={row.days} className='grid grid-cols-[1fr_auto] gap-6 border-b border-hair py-5'>
                                <dt>
                                    <span className='block font-heading text-2xl text-star'>
                                        {row.days}
                                    </span>
                                    <span className='block text-sm text-star-3'>
                                        {row.note}
                                    </span>
                                </dt>
                                <dd className='data-mono text-lg text-star-2'>
                                    {row.hours}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    <p className='mt-5 text-sm text-star-3'>
                        Closed 24 to 26 December and 1 January.
                    </p>
                </div>
            </section>

            <section aria-labelledby='travel-title' className='container-page pb-24'>
                <ArcDivider className='mb-16' />
                <h2 id='travel-title' className='label-mono text-star-3'>
                    Getting here
                </h2>
                <div className='mt-8 grid gap-10 md:grid-cols-3'>
                    {TRAVEL.map(item => (
                        <div key={item.title} className='border-t border-hair pt-6'>
                            <h3 className='font-heading text-3xl text-star'>
                                {item.title}
                            </h3>
                            <p className='mt-3 text-[0.95rem] leading-relaxed text-star-2'>
                                {item.text}
                            </p>
                        </div>
                    ))}
                </div>
                <p className='mt-10 max-w-2xl text-star-2'>
                    Free parking for forty cars and two coaches, with low red lighting and four accessible bays beside the entrance.
                </p>
            </section>

            <section id='accessibility' aria-labelledby='access-title' className='container-page scroll-mt-24 grid gap-14 pb-24 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <h2 id='access-title' className='display-serif text-5xl text-star'>
                        Accessibility
                    </h2>
                    <p className='mt-4 text-star-2'>
                        Everyone should be able to see the sky. If something here does not work for you, tell us before you come and we will do our best to make it work.
                    </p>
                </div>
                <ul className='lg:col-span-7'>
                    {ACCESS.map(item => (
                        <li key={item} className='flex gap-4 border-b border-hair py-4 text-star-2'>
                            <span aria-hidden className='mt-[0.75rem] h-px w-4 shrink-0 bg-brass' />
                            {item}
                        </li>
                    ))}
                </ul>
            </section>

            <section id='dress' aria-labelledby='dress-title' className='container-page scroll-mt-24 grid gap-14 pb-24 lg:grid-cols-12'>
                <Reveal className='lg:col-span-5'>
                    <Media src='/images/telescope-deck.jpg' alt='The roof deck at night' sizes='(min-width: 1024px) 40vw, 100vw' className='aspect-[4/5] w-full' />
                </Reveal>
                <div className='flex flex-col justify-center lg:col-span-7'>
                    <h2 id='dress-title' className='display-serif text-5xl text-star'>
                        Dressing for the roof
                    </h2>
                    <p className='mt-5 text-star-2'>
                        The deck sits 420 metres up on an open ridge. Even on a mild evening it is several degrees colder than town, and standing still at an eyepiece is colder still. Dress as if for a winter walk, whatever the season.
                    </p>
                    <ul className='mt-8 grid gap-3 sm:grid-cols-2'>
                        {['A warm hat and gloves', 'One more layer than you think', 'Sturdy shoes with a good grip', 'A flask of something hot', 'A red torch, or borrow ours', 'A blanket for meteor nights'].map(item => (
                            <li key={item} className='flex gap-3 text-star'>
                                <span aria-hidden className='mt-[0.7rem] h-px w-3 shrink-0 bg-brass' />
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-labelledby='etiquette-title' className='container-page pb-24'>
                <h2 id='etiquette-title' className='label-mono text-star-3'>
                    A dark sky site
                </h2>
                <p className='mt-4 flex items-center gap-3 font-heading text-3xl text-star md:text-4xl'>
                    <span aria-hidden className='size-2 shrink-0 rounded-full bg-signal' />
                    Dark sky site. Please use red torches only.
                </p>
                <div className='mt-8 grid gap-10 md:grid-cols-3'>
                    {ETIQUETTE.map(item => (
                        <div key={item.title} className='border-t border-hair pt-6'>
                            <h3 className='flex items-center gap-3 font-heading text-3xl text-star'>
                                <span aria-hidden className='size-2 rounded-full bg-signal' />
                                {item.title}
                            </h3>
                            <p className='mt-3 text-[0.95rem] leading-relaxed text-star-2'>
                                {item.text}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            <section id='contact' aria-labelledby='contact-title' className='container-page scroll-mt-24 pb-32'>
                <RingsDivider className='mb-16' />
                <div className='grid gap-14 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <p className='label-mono text-brass'>
                            Write to us
                        </p>
                        <h2 id='contact-title' className='display-serif mt-3 text-5xl text-star'>
                            Questions, groups and private hire
                        </h2>
                        <p className='mt-5 text-star-2'>
                            For school visits, private shows, accessibility needs or anything else, send us a note. One of the team will reply within two working days.
                        </p>
                    </div>
                    <div className='relative lg:col-span-7'>
                        <ContactForm />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default Visit
